import { normalizeSlotLabel } from "./slotLabel";
import {
  DEFAULT_VARIANT_CONFIG,
  normalizeVariantConfig,
  type VariantConfig,
  type VariantTransformRule,
} from "./variantConfig";

export type NamedVariantMaterialsEffectMode = "transform" | "delta" | "hybrid";

export type NamedVariantDeltaLine = {
  material_id?: string | null;
  material_use_id?: string | null;
  material_code?: string;
  material_name?: string;
  component_recipe_id?: string | null;
  component_recipe_code?: string;
  quantity: number;
  slot_label?: string;
  role?: string;
};

export type NamedVariantMaterialsEffect = {
  mode: NamedVariantMaterialsEffectMode;
  transform_rules?: VariantTransformRule[];
  replaces_slot_labels?: string[];
  delta_lines?: NamedVariantDeltaLine[];
  /** Deal fields that select this confirmed variant. Example: `{ width: "10" }`. */
  match?: Record<string, string>;
};

export const EMPTY_MATERIALS_EFFECT: NamedVariantMaterialsEffect = { mode: "delta" };

function normalizeDeltaLine(raw: unknown): NamedVariantDeltaLine | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const quantity = Number(r.quantity);
  if (!Number.isFinite(quantity) || quantity === 0) return null;
  const material_id =
    typeof r.material_id === "string" && r.material_id.trim()
      ? r.material_id.trim()
      : null;
  const material_use_id =
    typeof r.material_use_id === "string" && r.material_use_id.trim()
      ? r.material_use_id.trim()
      : null;
  const material_code = typeof r.material_code === "string" ? r.material_code : undefined;
  const material_name = typeof r.material_name === "string" ? r.material_name : undefined;
  const component_recipe_id =
    typeof r.component_recipe_id === "string" && r.component_recipe_id.trim()
      ? r.component_recipe_id.trim()
      : null;
  const component_recipe_code =
    typeof r.component_recipe_code === "string" && r.component_recipe_code.trim()
      ? r.component_recipe_code.trim()
      : undefined;
  if (
    !material_id &&
    !material_use_id &&
    !material_code?.trim() &&
    !material_name?.trim() &&
    !component_recipe_id &&
    !component_recipe_code
  ) {
    return null;
  }
  return {
    material_id,
    material_use_id,
    material_code,
    material_name,
    component_recipe_id,
    component_recipe_code,
    quantity,
    slot_label:
      typeof r.slot_label === "string" ? normalizeSlotLabel(r.slot_label) : undefined,
    role: typeof r.role === "string" ? r.role : undefined,
  };
}

/** Pure delta never strips parent slots; transform replaces only ruled slots. */
function keepMatch(
  effect: NamedVariantMaterialsEffect,
  match: NamedVariantMaterialsEffect["match"],
): NamedVariantMaterialsEffect {
  return match && Object.keys(match).length > 0 ? { ...effect, match } : effect;
}

export function coerceMaterialsEffect(
  effect: NamedVariantMaterialsEffect,
): NamedVariantMaterialsEffect {
  return keepMatch(coerceMaterialsEffectBody(effect), effect.match);
}

function coerceMaterialsEffectBody(
  effect: NamedVariantMaterialsEffect,
): NamedVariantMaterialsEffect {
  if (effect.mode === "delta") {
    return {
      mode: "delta",
      delta_lines: effect.delta_lines?.length ? effect.delta_lines : undefined,
    };
  }
  if (effect.mode === "transform") {
    const rules = effect.transform_rules?.length ? effect.transform_rules : undefined;
    if (!rules) return { mode: "delta", delta_lines: effect.delta_lines };
    const slots = [...new Set(rules.flatMap((r) => r.slots))];
    return {
      mode: "transform",
      transform_rules: rules,
      replaces_slot_labels: slots.length > 0 ? slots : undefined,
    };
  }
  const rules = effect.transform_rules?.length ? effect.transform_rules : undefined;
  const deltas = effect.delta_lines?.length ? effect.delta_lines : undefined;
  const explicitReplace = (effect.replaces_slot_labels ?? []).filter((s) => s.length > 0);
  // Slot replacement with explicit labels and delta lines, no lumber transform.
  // Pure delta still cannot strip slots; this path is only hybrid.
  if (!rules && deltas && explicitReplace.length > 0) {
    return {
      mode: "hybrid",
      replaces_slot_labels: explicitReplace,
      delta_lines: deltas,
    };
  }
  if (!rules && deltas) return { mode: "delta", delta_lines: deltas };
  if (!rules && !deltas) return { mode: "delta" };
  const slots = [...new Set((rules ?? []).flatMap((r) => r.slots))];
  return {
    mode: "hybrid",
    transform_rules: rules,
    replaces_slot_labels: slots.length > 0 ? slots : undefined,
    delta_lines: deltas,
  };
}

function normalizeEffectTransformRule(raw: unknown): VariantTransformRule | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const slotsRaw = r.slots;
  const slots = Array.isArray(slotsRaw)
    ? slotsRaw
        .map((s) => (typeof s === "string" ? normalizeSlotLabel(s) : ""))
        .filter((s) => s.length > 0)
    : [];
  // Application drafts may clear unmatched from/to after Apply — keep the rule when slots exist.
  if (slots.length === 0) return null;
  const fromNominal =
    typeof r.fromNominal === "string"
      ? r.fromNominal.trim()
      : typeof r.from === "string"
        ? r.from.trim()
        : "";
  const toNominal =
    typeof r.toNominal === "string"
      ? r.toNominal.trim()
      : typeof r.to === "string"
        ? r.to.trim()
        : "";
  return { slots, fromNominal, toNominal };
}

export function normalizeMaterialsEffect(raw: unknown): NamedVariantMaterialsEffect {
  if (!raw || typeof raw !== "object") return { ...EMPTY_MATERIALS_EFFECT };
  const r = raw as Record<string, unknown>;
  const modeRaw = typeof r.mode === "string" ? r.mode : "delta";
  const mode: NamedVariantMaterialsEffectMode =
    modeRaw === "transform" || modeRaw === "hybrid" || modeRaw === "delta"
      ? modeRaw
      : "delta";

  const rulesRaw = Array.isArray(r.transform_rules) ? r.transform_rules : [];
  const transform_rules = rulesRaw
    .map(normalizeEffectTransformRule)
    .filter((rule): rule is VariantTransformRule => rule != null);

  const replaces = Array.isArray(r.replaces_slot_labels)
    ? r.replaces_slot_labels
        .map((s) => (typeof s === "string" ? normalizeSlotLabel(s) : ""))
        .filter((s) => s.length > 0)
    : [];

  const delta_lines = Array.isArray(r.delta_lines)
    ? r.delta_lines
        .map(normalizeDeltaLine)
        .filter((ln): ln is NamedVariantDeltaLine => ln != null)
    : [];

  const match = variantMatchRecord(r.match);
  return coerceMaterialsEffect({
    mode,
    transform_rules: transform_rules.length > 0 ? transform_rules : undefined,
    replaces_slot_labels: replaces.length > 0 ? replaces : undefined,
    delta_lines: delta_lines.length > 0 ? delta_lines : undefined,
    match,
  });
}

function variantMatchRecord(raw: unknown): Record<string, string> | undefined {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return undefined;
  const match: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const name = key.trim();
    if (!name) continue;
    if (typeof value === "string" && value.trim()) match[name] = value.trim();
    else if (typeof value === "number" && Number.isFinite(value)) match[name] = String(value);
  }
  return Object.keys(match).length > 0 ? match : undefined;
}

function catalogDeltasToEffectLines(
  lines: NonNullable<Extract<VariantConfig, { mode: "delta" }>["delta_lines"]>,
) {
  return lines.map((ln) => ({
    material_id: ln.material_id,
    material_use_id: ln.material_use_id,
    material_code: ln.material_code || undefined,
    material_name: ln.material_name || undefined,
    quantity: ln.quantity,
    slot_label: ln.slot_label || undefined,
    role: "structural",
  }));
}

export function variantConfigToMaterialsEffect(config: VariantConfig): NamedVariantMaterialsEffect {
  const cfg = normalizeVariantConfig(config);
  if (cfg.mode === "transform") {
    const slots = [...new Set(cfg.rules.flatMap((r) => r.slots))];
    return {
      mode: "transform",
      transform_rules: cfg.rules,
      replaces_slot_labels: slots.length > 0 ? slots : undefined,
    };
  }
  // Fixed catalog deltas only — slot_scale_rules / proportional_add_rules need
  // parent BOM context at apply time.
  const delta_lines = catalogDeltasToEffectLines(cfg.delta_lines ?? []);
  if (cfg.mode === "hybrid") {
    return coerceMaterialsEffect({
      mode: "hybrid",
      transform_rules: cfg.rules,
      delta_lines: delta_lines.length > 0 ? delta_lines : undefined,
    });
  }
  return {
    mode: "delta",
    delta_lines: delta_lines.length > 0 ? delta_lines : undefined,
  };
}

export function materialsEffectToVariantConfig(
  effect: NamedVariantMaterialsEffect,
): VariantConfig {
  const coerced = coerceMaterialsEffect(effect);
  const mappedDeltas = (coerced.delta_lines ?? []).map((ln) => ({
    material_id: ln.material_id ?? null,
    material_use_id: ln.material_use_id ?? null,
    material_code: ln.material_code ?? "",
    material_name: ln.material_name ?? "",
    quantity: ln.quantity,
    slot_label: ln.slot_label ?? "",
  }));
  if (coerced.mode === "hybrid" && coerced.transform_rules && coerced.transform_rules.length > 0) {
    return {
      mode: "hybrid",
      rules: coerced.transform_rules,
      ...(mappedDeltas.length > 0 ? { delta_lines: mappedDeltas } : {}),
    };
  }
  if (coerced.mode === "transform" && coerced.transform_rules && coerced.transform_rules.length > 0) {
    return { mode: "transform", rules: coerced.transform_rules };
  }
  if (mappedDeltas.length > 0) {
    return { mode: "delta", delta_lines: mappedDeltas };
  }
  return DEFAULT_VARIANT_CONFIG;
}

export function summarizeMaterialsEffect(effect: NamedVariantMaterialsEffect): string {
  const coerced = coerceMaterialsEffect(effect);
  const parts: string[] = [];
  if (coerced.mode === "transform" || coerced.mode === "hybrid") {
    const n = coerced.transform_rules?.length ?? 0;
    if (n > 0) parts.push(`${n} transform rule${n === 1 ? "" : "s"}`);
  }
  if (coerced.mode === "delta" || coerced.mode === "hybrid") {
    const n = coerced.delta_lines?.length ?? 0;
    if (n > 0) parts.push(`${n} delta line${n === 1 ? "" : "s"}`);
  }
  const slots = coerced.replaces_slot_labels?.length ?? 0;
  if (slots > 0) parts.push(`replaces ${slots} slot${slots === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(" · ") : "No materials effect yet";
}
